import { startOfHour, subHours } from "@suse-o11y/date-time";
import { identity } from "@suse-o11y/identity";
import { tryCatch2v } from "fp-ts/es6/Either";
import isDate from "lodash/isDate";
import { decodeString, encodeNumber } from "serialize-query-params";
import type { BaseSchema, ValidationError } from "yup";

import { HealthStateValue } from "~/models/dto";

import { Api } from "../common/openapi";

export const PREVIOUS_FULL_HOUR = "PREVIOUS_FULL_HOUR";

export class URLParser {
	private readonly url: string;
	readonly protocol: string; // => "https:" contains the protocol scheme of the URL, including the final ':'.
	readonly hostname: string; // => "example.com"
	readonly port: string; // => "3000"
	readonly pathname: string; // => "/pathname/"
	readonly search: string; // => "?search=test"
	readonly hash: string; // => "#hash"
	readonly host: string; // => "example.com: 3000"

	constructor(urlString: string) {
		const location = document.createElement("a");
		location.href = urlString;

		this.url = urlString;
		this.protocol = location.protocol;
		this.host = location.host;
		this.hostname = location.hostname;
		this.pathname = location.pathname;
		this.port = location.port;
		this.search = location.search;
		this.hash = location.hash;
	}

	toString() {
		return this.url;
	}
}

export const isOnline = (): boolean => navigator.onLine;

const StateWeights = new Map<Api.HealthStateValue | HealthStateValue, number>([
	[HealthStateValue.Unknown, 3],
	[Api.HealthStateValue.UNKNOWN, 3],
	[HealthStateValue.Clear, 2],
	[Api.HealthStateValue.CLEAR, 2],
	[HealthStateValue.Deviating, 1],
	[Api.HealthStateValue.DEVIATING, 1],
	[HealthStateValue.Critical, 0],
	[Api.HealthStateValue.CRITICAL, 0],
]);

export const healthStateToStateWeight = (a: Api.HealthStateValue | HealthStateValue): number =>
	StateWeights.get(a) ?? StateWeights.size;

export function decodeTimeNumber(value: string | undefined): Date | undefined {
	const stringResult = decodeString(value);
	if (!stringResult) {
		return undefined;
	}

	let time = Date.now();
	const parts = stringResult.split("+");
	let tail = parts.slice(1);
	if (parts.length > 0) {
		const absoluteStr = parts[0];
		if (absoluteStr.match("^[0-9]+$")) {
			time = Number.parseInt(absoluteStr, 10);
		} else if (absoluteStr === PREVIOUS_FULL_HOUR) {
			time = startOfHour(subHours(time, 1)).getTime();
		} else {
			tail = parts;
		}
	}
	for (const part of tail) {
		const match = part.match(/^(?<sign>-?)(?<delta>[0-9]*)(?<unit>[hmd])$/u);
		if (match) {
			const sign = match.groups!.sign === "-" ? -1 : 1;
			const lastChar = match.groups!.unit;
			const unit = lastChar === "m" ? 60 : lastChar === "h" ? 60 * 60 : 24 * 60 * 60;
			time += sign * Number.parseInt(match.groups!.delta, 10) * unit * 1000;
		} else if (part === "") {
		} else {
			return undefined;
		}
	}
	return new Date(time);
}

export function encodeTimeNumber(value: Date | number | undefined): string | undefined {
	if (value == null) {
		return undefined;
	}
	const result = encodeNumber(isDate(value) ? value.getTime() : value);
	if (result == null) {
		return undefined;
	}
	return result;
}

export function mergeSchemas<T extends Array<BaseSchema<unknown>>>(...schemas: T): BaseSchema<unknown> {
	const [first, ...rest] = schemas;
	return rest.reduce((mergedSchemas, schema) => mergedSchemas.concat(schema), first);
}

export function validateSync<T>(schema: BaseSchema<T>, value: T): undefined | ValidationError {
	return tryCatch2v<ValidationError, undefined>(() => void schema.validateSync(value), identity<ValidationError>).value;
}
